import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Paper,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Divider,
  Stack,
  Grid,
  Card,
  CardActionArea,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import { ChevronLeft, ChevronRight, Close as CloseIcon } from '@mui/icons-material';
import dayjs from 'dayjs';
import {
  formatDateTime,
  getAircraftLabel,
  getStatusColor,
  renderApprovalChips
} from '../sales/SalesEnquiryUtils';

const WEEKDAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

const getTripDateKey = (trip) => {
  const raw = trip?.listFlightDate || trip?.flightDate || trip?.createdAt;
  if (!raw) return null;
  const parsed = dayjs(raw);
  return parsed.isValid() ? parsed.format('YYYY-MM-DD') : null;
};

const getTripAircraftId = (trip) => {
  const ref = trip?.listAircraftId || trip?.aircraftId;
  if (!ref) return null;
  return String(typeof ref === 'object' ? ref._id || ref : ref);
};

const getTripPilotIds = (trip) => {
  const refs = trip?.listAssignedPilotIds || trip?.assignedPilots || [];
  if (!Array.isArray(refs)) return [];
  return refs
    .map((ref) => {
      if (!ref) return null;
      return String(typeof ref === 'object' ? ref._id || ref : ref);
    })
    .filter(Boolean);
};

const truncateCharter = (value, max = 8) => {
  if (!value) return 'Trip';
  const text = String(value);
  return text.length > max ? `${text.slice(0, max - 1)}…` : text;
};

/** Registration / tail only (no manufacturer/model) for compact calendar chips. */
const getAircraftReg = (trip, aircraftList = []) => {
  if (!trip) return '';
  if (trip.listAircraftRegistration) return String(trip.listAircraftRegistration);
  const aircraftId = trip.listAircraftId || trip.aircraftId;
  if (!aircraftId) return '';
  const id = typeof aircraftId === 'string' ? aircraftId : aircraftId._id;
  const match = aircraftList.find((ac) => String(ac._id) === String(id));
  if (match) {
    return match.registration || match.tailNumber || '';
  }
  if (typeof aircraftId === 'object') {
    return aircraftId.registration || aircraftId.tailNumber || '';
  }
  return '';
};

const getCalendarTripLabel = (trip, aircraftList = [], max = 16) => {
  const reg = getAircraftReg(trip, aircraftList);
  const charter = trip?.charterNumber || trip?.title || 'Trip';
  const full = reg ? `${reg} ${charter}` : charter;
  return truncateCharter(full, max);
};

/** Day cell colors by charter count — light blue for 1; soft teal for 2; red / dark green / orange kept. */
const DENSITY_LEGEND = [
  { count: '0', borderColor: '#EF9A9A', bgcolor: 'rgba(211, 47, 47, 0.08)', label: '0' },
  { count: '1', borderColor: '#90CAF9', bgcolor: 'rgba(144, 202, 249, 0.28)', label: '1' },
  { count: '2', borderColor: '#588A87', bgcolor: 'rgba(88, 138, 135, 0.22)', label: '2' },
  { count: '3', borderColor: '#2E7D32', bgcolor: 'rgba(46, 125, 50, 0.3)', label: '3' },
  { count: '4+', borderColor: '#FB8C00', bgcolor: 'rgba(251, 140, 0, 0.24)', label: '4+' }
];

const FLIGHT_TYPE_STYLE = {
  CHARTER: {
    key: 'CHARTER',
    label: 'Charter',
    borderColor: '#1565C0',
    bgcolor: 'rgba(21, 101, 192, 0.22)',
    boxShadow: 'inset 0 0 0 1px rgba(21, 101, 192, 0.3)',
    textColor: '#0D47A1',
    hoverBg: 'rgba(21, 101, 192, 0.34)',
    hoverBorder: '#0D47A1',
    chipBg: 'rgba(21, 101, 192, 0.18)',
    chipColor: '#0D47A1'
  },
  NON_REVENUE: {
    key: 'NON_REVENUE',
    label: 'Non-Rev',
    borderColor: '#588A87',
    bgcolor: 'rgba(88, 138, 135, 0.22)',
    boxShadow: 'inset 0 0 0 1px rgba(88, 138, 135, 0.35)',
    textColor: '#3D6562',
    hoverBg: 'rgba(88, 138, 135, 0.34)',
    hoverBorder: '#4A7572',
    chipBg: 'rgba(88, 138, 135, 0.2)',
    chipColor: '#3D6562'
  },
  TRAINING: {
    key: 'TRAINING',
    label: 'Training',
    borderColor: '#2E7D32',
    bgcolor: 'rgba(46, 125, 50, 0.22)',
    boxShadow: 'inset 0 0 0 1px rgba(46, 125, 50, 0.32)',
    textColor: '#1B5E20',
    hoverBg: 'rgba(46, 125, 50, 0.34)',
    hoverBorder: '#1B5E20',
    chipBg: 'rgba(46, 125, 50, 0.18)',
    chipColor: '#1B5E20'
  },
  FERRY: {
    key: 'FERRY',
    label: 'Ferry',
    borderColor: '#EF6C00',
    bgcolor: 'rgba(239, 108, 0, 0.2)',
    boxShadow: 'inset 0 0 0 1px rgba(239, 108, 0, 0.3)',
    textColor: '#E65100',
    hoverBg: 'rgba(239, 108, 0, 0.32)',
    hoverBorder: '#E65100',
    chipBg: 'rgba(239, 108, 0, 0.18)',
    chipColor: '#E65100'
  },
  POSITIONING: {
    key: 'POSITIONING',
    label: 'Position',
    borderColor: '#6A4C93',
    bgcolor: 'rgba(106, 76, 147, 0.18)',
    boxShadow: 'inset 0 0 0 1px rgba(106, 76, 147, 0.28)',
    textColor: '#4A2C6A',
    hoverBg: 'rgba(106, 76, 147, 0.3)',
    hoverBorder: '#4A2C6A',
    chipBg: 'rgba(106, 76, 147, 0.16)',
    chipColor: '#4A2C6A'
  },
  MAINTENANCE: {
    key: 'MAINTENANCE',
    label: 'Maint.',
    borderColor: '#C62828',
    bgcolor: 'rgba(198, 40, 40, 0.16)',
    boxShadow: 'inset 0 0 0 1px rgba(198, 40, 40, 0.28)',
    textColor: '#B71C1C',
    hoverBg: 'rgba(198, 40, 40, 0.28)',
    hoverBorder: '#B71C1C',
    chipBg: 'rgba(198, 40, 40, 0.14)',
    chipColor: '#B71C1C'
  },
  MIXED: {
    key: 'MIXED',
    label: 'Mixed',
    borderColor: '#78909C',
    bgcolor: 'rgba(120, 144, 156, 0.2)',
    boxShadow: 'inset 0 0 0 1px rgba(120, 144, 156, 0.3)',
    textColor: '#455A64',
    hoverBg: 'rgba(120, 144, 156, 0.32)',
    hoverBorder: '#546E7A',
    chipBg: 'rgba(120, 144, 156, 0.18)',
    chipColor: '#455A64'
  }
};

const FLIGHT_TYPE_LEGEND = [
  FLIGHT_TYPE_STYLE.CHARTER,
  FLIGHT_TYPE_STYLE.NON_REVENUE,
  FLIGHT_TYPE_STYLE.TRAINING,
  FLIGHT_TYPE_STYLE.FERRY,
  FLIGHT_TYPE_STYLE.POSITIONING,
  FLIGHT_TYPE_STYLE.MAINTENANCE,
  FLIGHT_TYPE_STYLE.MIXED
];

const EMPTY_DAY_STYLE = {
  borderColor: 'error.light',
  bgcolor: 'rgba(211, 47, 47, 0.04)',
  boxShadow: 'inset 0 0 0 1px rgba(211, 47, 47, 0.15)',
  textColor: 'text.primary',
  hoverBg: 'rgba(211, 47, 47, 0.1)',
  hoverBorder: 'error.main',
  chipColor: 'default'
};

const normalizeFlightType = (trip) => {
  const raw = String(trip?.flightType || 'CHARTER').toUpperCase();
  return FLIGHT_TYPE_STYLE[raw] ? raw : 'CHARTER';
};

const getDominantFlightType = (dayTrips = []) => {
  if (!dayTrips.length) return null;
  const counts = {};
  dayTrips.forEach((trip) => {
    const type = normalizeFlightType(trip);
    counts[type] = (counts[type] || 0) + 1;
  });
  const distinct = Object.keys(counts);
  if (distinct.length > 1) {
    const max = Math.max(...distinct.map((k) => counts[k]));
    const leaders = distinct.filter((k) => counts[k] === max);
    if (leaders.length > 1) return 'MIXED';
  }
  return distinct.sort((a, b) => counts[b] - counts[a])[0];
};

const getDayDensityStyle = (tripCount) => {
  if (!tripCount || tripCount <= 0) {
    return EMPTY_DAY_STYLE;
  }
  if (tripCount === 1) {
    // Light blue
    return {
      borderColor: '#90CAF9',
      bgcolor: 'rgba(144, 202, 249, 0.28)',
      boxShadow: 'inset 0 0 0 1px rgba(100, 181, 246, 0.35)',
      textColor: '#1565C0',
      hoverBg: 'rgba(144, 202, 249, 0.42)',
      hoverBorder: '#64B5F6',
      chipColor: 'info'
    };
  }
  if (tripCount === 2) {
    // Muted teal from palette (#588A87)
    return {
      borderColor: '#588A87',
      bgcolor: 'rgba(88, 138, 135, 0.22)',
      boxShadow: 'inset 0 0 0 1px rgba(88, 138, 135, 0.35)',
      textColor: '#3D6562',
      hoverBg: 'rgba(88, 138, 135, 0.34)',
      hoverBorder: '#4A7572',
      chipColor: 'success'
    };
  }
  if (tripCount === 3) {
    return {
      borderColor: '#2E7D32',
      bgcolor: 'rgba(46, 125, 50, 0.3)',
      boxShadow: 'inset 0 0 0 1px rgba(27, 94, 32, 0.35)',
      textColor: '#1B5E20',
      hoverBg: 'rgba(46, 125, 50, 0.42)',
      hoverBorder: '#1B5E20',
      chipColor: 'success'
    };
  }
  // 4+ — orange (busiest)
  return {
    borderColor: '#FB8C00',
    bgcolor: 'rgba(251, 140, 0, 0.24)',
    boxShadow: 'inset 0 0 0 1px rgba(239, 108, 0, 0.32)',
    textColor: '#E65100',
    hoverBg: 'rgba(251, 140, 0, 0.36)',
    hoverBorder: '#EF6C00',
    chipColor: 'warning'
  };
};

const getDayFlightTypeStyle = (dayTrips = []) => {
  if (!dayTrips.length) return EMPTY_DAY_STYLE;
  const dominant = getDominantFlightType(dayTrips);
  return FLIGHT_TYPE_STYLE[dominant] || FLIGHT_TYPE_STYLE.CHARTER;
};

const formatPerson = (person) => {
  if (!person) return '—';
  if (typeof person === 'string') return person;
  const name = `${person.firstName || ''} ${person.lastName || ''}`.trim();
  return name || person.email || '—';
};

const formatDateTimeFull = (date) => {
  if (!date) return '—';
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const resolvePilotNames = (pilotRefs = [], pilots = []) => {
  if (!Array.isArray(pilotRefs) || pilotRefs.length === 0) return 'Unassigned';
  return pilotRefs
    .map((ref) => {
      if (!ref) return null;
      if (typeof ref === 'object' && (ref.firstName || ref.lastName || ref.email)) {
        return formatPerson(ref);
      }
      const id = typeof ref === 'object' ? ref._id || ref : ref;
      const match = pilots.find((p) => String(p._id) === String(id));
      return match ? formatPerson(match) : String(id);
    })
    .filter(Boolean)
    .join(', ') || 'Unassigned';
};

const DetailRow = ({ label, value, fullWidth = false }) => (
  <Grid item xs={12} sm={fullWidth ? 12 : 6}>
    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.25 }}>
      {label}
    </Typography>
    {typeof value === 'string' || typeof value === 'number' ? (
      <Typography variant="body2" sx={{ fontWeight: 500, wordBreak: 'break-word' }}>
        {value || '—'}
      </Typography>
    ) : (
      value || (
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          —
        </Typography>
      )
    )}
  </Grid>
);

/**
 * Compact one-month calendar of trips, for use beside the trips/charters table.
 */
const TripsMonthCalendar = ({
  trips = [],
  selectedTrip = null,
  onTripClick,
  aircraft = [],
  pilots = [],
  embedded = false,
  enlarged = false,
  selectionMode = false,
  selectedDates = [],
  onToggleDate,
  maxSelected = 15,
  highlightTripIds = []
}) => {
  const [viewMonth, setViewMonth] = useState(() => dayjs().startOf('month'));
  const [dayDetail, setDayDetail] = useState(null); // { dateKey, day, trips }
  const [filterAircraftId, setFilterAircraftId] = useState('');
  const [filterPilotId, setFilterPilotId] = useState('');
  const [colorByFlightType, setColorByFlightType] = useState(false);

  const selectedDateSet = useMemo(() => new Set(selectedDates || []), [selectedDates]);
  const highlightIdSet = useMemo(
    () => new Set((highlightTripIds || []).map((id) => String(id))),
    [highlightTripIds]
  );

  const filteredTrips = useMemo(() => {
    return (trips || []).filter((trip) => {
      if (filterAircraftId) {
        const tripAircraftId = getTripAircraftId(trip);
        if (tripAircraftId !== String(filterAircraftId)) return false;
      }
      if (filterPilotId) {
        const tripPilotIds = getTripPilotIds(trip);
        if (!tripPilotIds.includes(String(filterPilotId))) return false;
      }
      return true;
    });
  }, [trips, filterAircraftId, filterPilotId]);

  const aircraftOptions = useMemo(() => {
    if (Array.isArray(aircraft) && aircraft.length > 0) {
      return aircraft.map((ac) => ({
        id: String(ac._id),
        label: getAircraftLabel(ac._id, aircraft)
      }));
    }
    // Fallback: unique aircraft ids from trips
    const seen = new Map();
    (trips || []).forEach((trip) => {
      const id = getTripAircraftId(trip);
      if (!id || seen.has(id)) return;
      seen.set(id, {
        id,
        label: getAircraftLabel(trip.listAircraftId || trip.aircraftId, aircraft)
      });
    });
    return Array.from(seen.values());
  }, [aircraft, trips]);

  const pilotOptions = useMemo(() => {
    if (Array.isArray(pilots) && pilots.length > 0) {
      return pilots.map((p) => ({
        id: String(p._id),
        label: formatPerson(p)
      }));
    }
    const seen = new Map();
    (trips || []).forEach((trip) => {
      getTripPilotIds(trip).forEach((id) => {
        if (seen.has(id)) return;
        const fromList = (trip.assignedPilots || []).find(
          (p) => String(p?._id || p) === id
        );
        seen.set(id, {
          id,
          label: fromList && typeof fromList === 'object' ? formatPerson(fromList) : id
        });
      });
    });
    return Array.from(seen.values());
  }, [pilots, trips]);

  const tripsByDate = useMemo(() => {
    const map = new Map();
    filteredTrips.forEach((trip) => {
      const key = getTripDateKey(trip);
      if (!key) return;
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(trip);
    });
    return map;
  }, [filteredTrips]);

  const calendarDays = useMemo(() => {
    const start = viewMonth.startOf('month');
    const end = viewMonth.endOf('month');
    const gridStart = start.startOf('week');
    const gridEnd = end.endOf('week');
    const days = [];
    let cursor = gridStart;
    while (cursor.isBefore(gridEnd) || cursor.isSame(gridEnd, 'day')) {
      days.push(cursor);
      cursor = cursor.add(1, 'day');
    }
    return days;
  }, [viewMonth]);

  const monthStats = useMemo(() => {
    let nonCancelled = 0;
    let completed = 0;
    filteredTrips.forEach((trip) => {
      const key = getTripDateKey(trip);
      if (!key) return;
      const tripDay = dayjs(key);
      if (!tripDay.isValid() || !tripDay.isSame(viewMonth, 'month')) return;
      const status = String(trip.status || '').toUpperCase();
      if (status !== 'CANCELLED') nonCancelled += 1;
      if (status === 'COMPLETED' || status === 'CHARTER_COMPLETED') completed += 1;
    });
    return { nonCancelled, completed };
  }, [filteredTrips, viewMonth]);

  const todayKey = dayjs().format('YYYY-MM-DD');
  const selectedKey = selectedTrip ? getTripDateKey(selectedTrip) : null;
  const hasActiveFilter = Boolean(filterAircraftId || filterPilotId);

  useEffect(() => {
    if (selectionMode) {
      setDayDetail(null);
    }
  }, [selectionMode]);

  const openDayDetail = (day, dayTrips) => {
    if (selectionMode) {
      const key = day.format('YYYY-MM-DD');
      if (typeof onToggleDate === 'function') {
        onToggleDate(key);
      }
      return;
    }
    if (!dayTrips?.length) return;
    setDayDetail({
      dateKey: day.format('YYYY-MM-DD'),
      day,
      trips: dayTrips
    });
  };

  const closeDayDetail = () => setDayDetail(null);

  const handleSelectTrip = (tripId) => {
    closeDayDetail();
    if (onTripClick) onTripClick(tripId);
  };

  return (
    <>
      <Paper
        variant={embedded ? 'elevation' : 'outlined'}
        elevation={0}
        sx={{
          p: embedded ? 0 : 1.5,
          height: enlarged ? '100%' : embedded ? 'auto' : '100%',
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          boxSizing: 'border-box',
          border: embedded ? 'none' : undefined,
          bgcolor: embedded ? 'transparent' : undefined
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5, flexShrink: 0 }}>
          <IconButton
            size="small"
            aria-label="Previous month"
            onClick={() => setViewMonth((m) => m.subtract(1, 'month'))}
          >
            <ChevronLeft fontSize="small" />
          </IconButton>
          <Box sx={{ textAlign: 'center', minWidth: 0, px: 0.5 }}>
            <Typography variant={enlarged ? 'h6' : 'subtitle2'} sx={{ fontWeight: 600, lineHeight: 1.2 }}>
              {viewMonth.format('MMM YYYY')}
            </Typography>
            <Stack
              direction="row"
              spacing={0.5}
              justifyContent="center"
              alignItems="center"
              flexWrap="wrap"
              useFlexGap
              sx={{ mt: 0.4 }}
            >
              <Chip
                size="small"
                label={`Active ${monthStats.nonCancelled}`}
                title="Non-cancelled charters this month"
                sx={{
                  height: 18,
                  fontSize: '0.62rem',
                  fontWeight: 700,
                  bgcolor: 'rgba(46, 125, 50, 0.12)',
                  color: 'success.dark',
                  '& .MuiChip-label': { px: 0.6 }
                }}
              />
              <Chip
                size="small"
                label={`Completed ${monthStats.completed}`}
                title="Completed charters this month"
                sx={{
                  height: 18,
                  fontSize: '0.62rem',
                  fontWeight: 700,
                  bgcolor: 'rgba(25, 118, 210, 0.12)',
                  color: 'primary.dark',
                  '& .MuiChip-label': { px: 0.6 }
                }}
              />
              {!selectionMode && (
                <Button
                  size="small"
                  variant={colorByFlightType ? 'contained' : 'outlined'}
                  onClick={() => setColorByFlightType((v) => !v)}
                  title={
                    colorByFlightType
                      ? 'Showing colors by flight type — click for charter count'
                      : 'Color calendar by flight type'
                  }
                  sx={{
                    height: 20,
                    minWidth: 0,
                    px: 0.8,
                    py: 0,
                    fontSize: '0.62rem',
                    fontWeight: 700,
                    lineHeight: 1.2,
                    textTransform: 'none',
                    borderRadius: 1
                  }}
                >
                  Flight Type
                </Button>
              )}
            </Stack>
          </Box>
          <IconButton
            size="small"
            aria-label="Next month"
            onClick={() => setViewMonth((m) => m.add(1, 'month'))}
          >
            <ChevronRight fontSize="small" />
          </IconButton>
        </Box>

        <Stack
          direction={enlarged ? { xs: 'column', sm: 'row' } : 'column'}
          spacing={1}
          sx={{ mb: 1, flexShrink: 0, alignItems: enlarged ? 'center' : 'stretch' }}
        >
          <FormControl size="small" fullWidth={!enlarged} sx={enlarged ? { flex: 1, minWidth: 0 } : undefined}>
            <InputLabel id="calendar-aircraft-filter-label">Aircraft (optional)</InputLabel>
            <Select
              labelId="calendar-aircraft-filter-label"
              label="Aircraft (optional)"
              value={filterAircraftId}
              onChange={(e) => setFilterAircraftId(e.target.value)}
            >
              <MenuItem value="">
                <em>All aircraft</em>
              </MenuItem>
              {aircraftOptions.map((opt) => (
                <MenuItem key={opt.id} value={opt.id}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" fullWidth={!enlarged} sx={enlarged ? { flex: 1, minWidth: 0 } : undefined}>
            <InputLabel id="calendar-crew-filter-label">Crew (optional)</InputLabel>
            <Select
              labelId="calendar-crew-filter-label"
              label="Crew (optional)"
              value={filterPilotId}
              onChange={(e) => setFilterPilotId(e.target.value)}
            >
              <MenuItem value="">
                <em>All crew</em>
              </MenuItem>
              {pilotOptions.map((opt) => (
                <MenuItem key={opt.id} value={opt.id}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {hasActiveFilter && (
            <Button
              size="small"
              onClick={() => {
                setFilterAircraftId('');
                setFilterPilotId('');
              }}
              sx={{ alignSelf: enlarged ? 'center' : 'flex-start', flexShrink: 0 }}
            >
              Clear filters
            </Button>
          )}
        </Stack>

        {!selectionMode && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 0.5,
              width: '100%',
              mb: 1,
              flexShrink: 0
            }}
          >
            {(colorByFlightType ? FLIGHT_TYPE_LEGEND : DENSITY_LEGEND).map((item) => (
              <Box
                key={item.key || item.count}
                title={item.label}
                sx={{
                  flex: 1,
                  minWidth: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 0.4,
                  border: '1.5px solid',
                  borderColor: item.borderColor,
                  bgcolor: item.bgcolor,
                  borderRadius: 0.5,
                  py: 0.2,
                  px: 0.25
                }}
              >
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '2px',
                    bgcolor: item.borderColor,
                    flexShrink: 0
                  }}
                />
                <Typography
                  variant="caption"
                  sx={{
                    fontSize: colorByFlightType ? '0.55rem' : '0.65rem',
                    fontWeight: 700,
                    lineHeight: 1.2,
                    color: 'text.primary',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {item.label}
                </Typography>
              </Box>
            ))}
          </Box>
        )}

        <Box
          sx={{
            border: '2px solid',
            borderColor: 'grey.700',
            borderRadius: 1,
            overflow: 'hidden',
            flex: embedded || enlarged ? (enlarged ? 1 : '0 0 auto') : 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column'
          }}
        >
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(7, 1fr)',
              borderBottom: '2px solid',
              borderColor: 'grey.700',
              bgcolor: 'grey.50',
              flexShrink: 0
            }}
          >
            {WEEKDAYS.map((d, idx) => (
              <Typography
                key={d}
                variant="caption"
                align="center"
                sx={{
                  fontWeight: 700,
                  color: 'text.secondary',
                  fontSize: enlarged ? '0.8rem' : '0.7rem',
                  py: enlarged ? 0.9 : 0.6,
                  borderRight: idx < 6 ? '1px solid' : 'none',
                  borderColor: 'grey.300'
                }}
              >
                {d}
              </Typography>
            ))}
          </Box>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(7, 1fr)',
              gridAutoRows: enlarged
                ? 'minmax(72px, 1fr)'
                : embedded
                  ? 'minmax(44px, auto)'
                  : '1fr',
              gap: 0,
              flex: enlarged || !embedded ? 1 : '0 0 auto',
              minHeight: enlarged ? 0 : embedded ? 0 : 280
            }}
          >
            {calendarDays.map((day, index) => {
              const key = day.format('YYYY-MM-DD');
              const inMonth = day.month() === viewMonth.month();
              const dayTrips = tripsByDate.get(key) || [];
              const isToday = key === todayKey;
              const hasSelection = selectedKey === key;
              const isDateSelected = selectionMode && selectedDateSet.has(key);
              const tripCount = dayTrips.length;
              const hasCharter = tripCount > 0;
              const density = colorByFlightType
                ? getDayFlightTypeStyle(dayTrips)
                : getDayDensityStyle(tripCount);
              const visibleTrips = dayTrips.slice(0, 2);
              const extraCount = tripCount - visibleTrips.length;
              const isOpenDay = !selectionMode && dayDetail?.dateKey === key;
              const col = index % 7;
              const isLastRow = index >= calendarDays.length - 7;
              const canClickDay = selectionMode || hasCharter;

              return (
                <Box
                  key={key}
                  sx={{
                    height: '100%',
                    minHeight: 0,
                    borderRight: col < 6 ? '1px solid' : 'none',
                    borderBottom: isLastRow ? 'none' : '1px solid',
                    borderColor: 'grey.300'
                  }}
                >
                  <Box
                    onClick={() => {
                      if (!canClickDay) return;
                      openDayDetail(day, dayTrips);
                    }}
                    sx={{
                      height: '100%',
                      minHeight: enlarged ? 68 : 48,
                      borderRadius: 0,
                      border: '2px solid',
                      borderColor: isDateSelected
                        ? 'secondary.main'
                        : isOpenDay
                          ? 'primary.main'
                          : hasSelection
                            ? 'primary.main'
                            : density.borderColor,
                      m: '1px',
                      bgcolor: isDateSelected
                        ? 'rgba(156, 39, 176, 0.18)'
                        : isOpenDay
                          ? 'rgba(25, 118, 210, 0.2)'
                          : hasSelection
                            ? 'rgba(25, 118, 210, 0.16)'
                            : isToday && !hasCharter
                              ? 'rgba(211, 47, 47, 0.08)'
                              : density.bgcolor,
                      boxShadow: isDateSelected
                        ? 'inset 0 0 0 1px rgba(156, 39, 176, 0.35)'
                        : density.boxShadow,
                      opacity: inMonth ? 1 : 0.4,
                      cursor: canClickDay ? 'pointer' : 'default',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'stretch',
                      justifyContent: 'flex-start',
                      p: 0.4,
                      overflow: 'hidden',
                      boxSizing: 'border-box',
                      '&:hover': canClickDay
                        ? {
                            bgcolor: isDateSelected
                              ? 'rgba(156, 39, 176, 0.26)'
                              : density.hoverBg,
                            borderColor: isDateSelected
                              ? 'secondary.dark'
                              : density.hoverBorder
                          }
                        : undefined
                    }}
                  >
                    <Box
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        alignSelf: 'flex-start',
                        gap: 0.35,
                        mb: hasCharter ? 0.4 : 0,
                        px: 0.55,
                        py: 0.15,
                        borderRadius: 0.75,
                        bgcolor: isToday
                          ? 'primary.main'
                          : isDateSelected || hasSelection || isOpenDay
                            ? 'rgba(0, 0, 0, 0.08)'
                            : 'rgba(0, 0, 0, 0.06)',
                        color: isToday ? '#fff' : undefined
                      }}
                    >
                      <Typography
                        component="span"
                        sx={{
                          fontSize: enlarged ? '0.95rem' : '0.85rem',
                          fontWeight: 800,
                          lineHeight: 1.15,
                          letterSpacing: 0.2,
                          color: isToday
                            ? '#fff'
                            : isDateSelected
                              ? 'secondary.dark'
                              : hasSelection || isOpenDay
                                ? 'primary.main'
                                : hasCharter
                                  ? density.textColor
                                  : 'text.primary'
                        }}
                      >
                        {day.date()}
                      </Typography>
                      {hasCharter && (
                        <Typography
                          component="span"
                          sx={{
                            fontSize: '0.62rem',
                            fontWeight: 800,
                            lineHeight: 1,
                            color: isToday ? 'rgba(255,255,255,0.92)' : density.textColor,
                            opacity: 0.95
                          }}
                        >
                          ·{tripCount}
                        </Typography>
                      )}
                    </Box>

                    {hasCharter && (
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 0.25,
                          flex: 1,
                          minHeight: 0,
                          overflow: 'hidden'
                        }}
                      >
                        {visibleTrips.map((trip) => {
                          const isSelectedTrip = selectedTrip?._id === trip._id;
                          const isHighlightedClone = highlightIdSet.has(String(trip._id));
                          const charterLabel = getCalendarTripLabel(trip, aircraft);
                          const flightTypeStyle = FLIGHT_TYPE_STYLE[normalizeFlightType(trip)];
                          const fullLabel = (() => {
                            const reg = getAircraftReg(trip, aircraft);
                            const charter = trip.charterNumber || trip.title || 'Trip';
                            const typeLabel = flightTypeStyle?.label || trip.flightType || 'Charter';
                            const base = reg ? `${reg} · ${charter}` : charter;
                            return colorByFlightType ? `${base} (${typeLabel})` : base;
                          })();
                          const useFlightTypeChip =
                            colorByFlightType && !isSelectedTrip && !isHighlightedClone;
                          return (
                            <Chip
                              key={trip._id}
                              label={charterLabel}
                              title={fullLabel}
                              size="small"
                              color={
                                isHighlightedClone
                                  ? 'secondary'
                                  : isSelectedTrip
                                    ? 'primary'
                                    : useFlightTypeChip
                                      ? 'default'
                                      : density.chipColor || 'default'
                              }
                              sx={{
                                height: 18,
                                maxWidth: '100%',
                                fontSize: '0.58rem',
                                fontWeight: 700,
                                borderRadius: 0.75,
                                pointerEvents: 'none',
                                ...(useFlightTypeChip
                                  ? {
                                      bgcolor: flightTypeStyle.chipBg,
                                      color: flightTypeStyle.chipColor,
                                      border: `1px solid ${flightTypeStyle.borderColor}`
                                    }
                                  : {}),
                                '& .MuiChip-label': {
                                  px: 0.5,
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap'
                                }
                              }}
                            />
                          );
                        })}
                        {extraCount > 0 && (
                          <Typography
                            variant="caption"
                            sx={{
                              fontSize: '0.58rem',
                              fontWeight: 700,
                              color: density.textColor,
                              lineHeight: 1.1,
                              pl: 0.25
                            }}
                          >
                            +{extraCount} more
                          </Typography>
                        )}
                      </Box>
                    )}
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Box>

        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ mt: 1, fontSize: '0.7rem', textAlign: 'center', flexShrink: 0 }}
        >
          {selectionMode
            ? `Tap days to select (${selectedDates.length}/${maxSelected}). Navigate months if needed.`
            : 'Legend = charters/day (red → blue → green → orange). Tap a day for details.'}
        </Typography>
      </Paper>

      <Dialog
        open={Boolean(dayDetail)}
        onClose={closeDayDetail}
        fullWidth
        maxWidth="md"
        aria-labelledby="day-trips-dialog-title"
      >
        <DialogTitle
          id="day-trips-dialog-title"
          sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 1 }}
        >
          <Box>
            <Typography variant="h6" component="span">
              {dayDetail?.day ? dayDetail.day.format('dddd, DD MMMM YYYY') : 'Day trips'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {dayDetail?.trips?.length || 0} charter
              {(dayDetail?.trips?.length || 0) === 1 ? '' : 's'} scheduled
            </Typography>
          </Box>
          <IconButton aria-label="Close" onClick={closeDayDetail} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ bgcolor: 'grey.50' }}>
          <Stack spacing={2}>
            {(dayDetail?.trips || []).map((trip) => {
              const isSelected = selectedTrip?._id === trip._id;
              const aircraftReg = getAircraftReg(trip, aircraft);
              const aircraftLabel = getAircraftLabel(
                trip.listAircraftId || trip.aircraftId,
                aircraft
              );
              const pilotLabel = resolvePilotNames(
                trip.listAssignedPilotIds || trip.assignedPilots || [],
                pilots
              );
              const flightDate = formatDateTime(trip.listFlightDate || trip.flightDate);
              const passengers = Array.isArray(trip.passengers) ? trip.passengers : [];
              const passengerSummary = passengers.length
                ? passengers
                    .map((p) => {
                      const bits = [
                        p.salutation,
                        p.name,
                        p.passengerType ? `(${p.passengerType})` : null,
                        p.weight != null ? `${p.weight}kg` : null
                      ].filter(Boolean);
                      return bits.join(' ');
                    })
                    .join('; ')
                : 'None listed';
              const contactBits = [
                trip.clientContact?.email,
                trip.clientContact?.phone
              ].filter(Boolean);
              const approvalChips = renderApprovalChips(
                trip.pendingApprovals || [],
                trip.approvalStatus || {},
                `day-${trip._id}`
              );

              return (
                <Card
                  key={trip._id}
                  variant="outlined"
                  sx={{
                    borderColor: isSelected ? 'primary.main' : 'divider',
                    borderWidth: isSelected ? 2 : 1,
                    bgcolor: 'background.paper'
                  }}
                >
                  <CardActionArea onClick={() => handleSelectTrip(trip._id)}>
                    <CardContent>
                      <Stack
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        flexWrap="wrap"
                        useFlexGap
                        sx={{ mb: 1.5 }}
                      >
                        <Typography variant="h6" fontWeight={700}>
                          {aircraftReg
                            ? `${aircraftReg} · ${trip.charterNumber || 'No charter #'}`
                            : (trip.charterNumber || 'No charter #')}
                        </Typography>
                        <Chip
                          label={trip.status || 'N/A'}
                          size="small"
                          color={getStatusColor(trip.status)}
                        />
                        <Chip
                          label={trip.flightType || 'CHARTER'}
                          size="small"
                          variant="outlined"
                        />
                        {isSelected && (
                          <Chip label="Currently selected" size="small" color="primary" />
                        )}
                      </Stack>

                      {trip.title && (
                        <Typography variant="subtitle1" sx={{ mb: 1.5 }}>
                          {trip.title}
                        </Typography>
                      )}

                      <Grid container spacing={1.5}>
                        <DetailRow label="Flight date" value={flightDate} />
                        <DetailRow label="Aircraft" value={aircraftLabel} />
                        <DetailRow label="Assigned pilots" value={pilotLabel} />
                        <DetailRow label="Client" value={trip.clientName || '—'} />
                        <DetailRow
                          label="Client contact"
                          value={contactBits.length ? contactBits.join(' · ') : '—'}
                        />
                        <DetailRow
                          label="Passengers"
                          value={`${passengers.length} passenger${passengers.length === 1 ? '' : 's'}`}
                        />
                        <DetailRow label="Passenger list" value={passengerSummary} fullWidth />
                        <DetailRow
                          label="Baggage weight"
                          value={
                            trip.baggageWeight != null ? `${trip.baggageWeight} kg` : '—'
                          }
                        />
                        <DetailRow
                          label="Total weight"
                          value={trip.totalWeight != null ? `${trip.totalWeight} kg` : '—'}
                        />
                        <DetailRow
                          label="Special requirements"
                          value={trip.specialRequirements || '—'}
                          fullWidth
                        />
                        <DetailRow
                          label="Itinerary notes"
                          value={trip.itineraryJourneyNotes || '—'}
                          fullWidth
                        />
                        <DetailRow
                          label="Itinerary display status"
                          value={trip.itineraryDisplayStatus || 'Auto from ops status'}
                        />
                        <DetailRow
                          label="BA / ATC required"
                          value={`BA: ${trip.requiresBA === false ? 'No' : 'Yes'} · ATC: ${
                            trip.requiresATCClearance === false ? 'No' : 'Yes'
                          }`}
                        />
                        <DetailRow label="Created by" value={formatPerson(trip.createdBy)} />
                        <DetailRow
                          label="Created at"
                          value={formatDateTimeFull(trip.createdAt)}
                        />
                        <DetailRow
                          label="Updated at"
                          value={formatDateTimeFull(trip.updatedAt)}
                        />
                        <DetailRow
                          label="Approvals"
                          fullWidth
                          value={
                            approvalChips?.length ? (
                              <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                                {approvalChips}
                              </Stack>
                            ) : (
                              'No pending approvals'
                            )
                          }
                        />
                      </Grid>

                      <Divider sx={{ my: 1.5 }} />
                      <Typography variant="body2" color="primary" fontWeight={700}>
                        Tap card to open this charter
                      </Typography>
                    </CardContent>
                  </CardActionArea>
                </Card>
              );
            })}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDayDetail}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default TripsMonthCalendar;
